import { firestore } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';

// Collection reference
const commissionsCollection = collection(firestore, 'commissions');

/**
 * Get all commissions with optional filters
 * @param {Object} filters - Optional filters (status, companyId, dateRange)
 * @returns {Promise<Array>} Array of commissions
 */
export const getAllCommissions = async (filters = {}) => {
  try {
    let q = query(commissionsCollection);
    
    // Apply filters
    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }
    
    if (filters.companyId) {
      q = query(q, where('companyId', '==', filters.companyId));
    }
    
    if (filters.startDate && filters.endDate) {
      q = query(
        q,
        where('createdAt', '>=', Timestamp.fromDate(new Date(filters.startDate))),
        where('createdAt', '<=', Timestamp.fromDate(new Date(filters.endDate)))
      );
    }
    
    // Order by creation date (newest first)
    q = query(q, orderBy('createdAt', 'desc'));
    
    // Apply limit if specified
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      paidAt: doc.data().paidAt?.toDate?.() || doc.data().paidAt
    }));
  } catch (error) {
    console.error('Error fetching commissions:', error);
    throw new Error('Erreur lors de la récupération des commissions');
  }
};

/**
 * Get a single commission by ID
 * @param {string} commissionId - Commission ID
 * @returns {Promise<Object>} Commission data
 */
export const getCommissionById = async (commissionId) => {
  try {
    const commissionDoc = await getDoc(doc(firestore, 'commissions', commissionId));
    
    if (!commissionDoc.exists()) {
      throw new Error('Commission non trouvée');
    }
    
    const data = commissionDoc.data();
    return {
      id: commissionDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      paidAt: data.paidAt?.toDate?.() || data.paidAt
    };
  } catch (error) {
    console.error('Error fetching commission:', error);
    throw new Error('Erreur lors de la récupération de la commission');
  }
};

/**
 * Get commissions by company ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} Array of commissions
 */
export const getCommissionsByCompany = async (companyId) => {
  try {
    const q = query(
      commissionsCollection,
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      paidAt: doc.data().paidAt?.toDate?.() || doc.data().paidAt
    }));
  } catch (error) {
    console.error('Error fetching company commissions:', error);
    throw new Error('Erreur lors de la récupération des commissions de l\'entreprise');
  }
};

/**
 * Create a new commission
 * @param {Object} commissionData - Commission data
 * @returns {Promise<Object>} Created commission with ID
 */
export const createCommission = async (commissionData) => {
  try {
    const now = new Date();
    
    const newCommission = {
      ...commissionData,
      status: commissionData.status || 'Pending',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now)
    };
    
    const docRef = await addDoc(commissionsCollection, newCommission);
    
    return {
      id: docRef.id,
      ...newCommission,
      createdAt: now,
      updatedAt: now
    };
  } catch (error) {
    console.error('Error creating commission:', error);
    throw new Error('Erreur lors de la création de la commission');
  }
};

/**
 * Update an existing commission
 * @param {string} commissionId - Commission ID
 * @param {Object} commissionData - Updated commission data
 * @returns {Promise<Object>} Updated commission
 */
export const updateCommission = async (commissionId, commissionData) => {
  try {
    const commissionRef = doc(firestore, 'commissions', commissionId);
    
    const updateData = {
      ...commissionData,
      updatedAt: Timestamp.fromDate(new Date())
    };
    
    // If status is being changed to 'Paid', add paidAt timestamp
    if (commissionData.status === 'Paid' && !commissionData.paidAt) {
      updateData.paidAt = Timestamp.fromDate(new Date());
    }
    
    await updateDoc(commissionRef, updateData);
    
    // Fetch and return updated document
    return await getCommissionById(commissionId);
  } catch (error) {
    console.error('Error updating commission:', error);
    throw new Error('Erreur lors de la mise à jour de la commission');
  }
};

/**
 * Delete a commission
 * @param {string} commissionId - Commission ID
 * @returns {Promise<void>}
 */
export const deleteCommission = async (commissionId) => {
  try {
    await deleteDoc(doc(firestore, 'commissions', commissionId));
  } catch (error) {
    console.error('Error deleting commission:', error);
    throw new Error('Erreur lors de la suppression de la commission');
  }
};

/**
 * Get commission statistics
 * @returns {Promise<Object>} Commission statistics
 */
export const getCommissionStats = async () => {
  try {
    const snapshot = await getDocs(commissionsCollection);
    const commissions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const stats = {
      total: commissions.length,
      totalAmount: 0,
      pending: 0,
      pendingAmount: 0,
      paid: 0,
      paidAmount: 0,
      cancelled: 0,
      cancelledAmount: 0
    };
    
    commissions.forEach(commission => {
      stats.totalAmount += commission.amount || 0;
      
      switch (commission.status) {
        case 'Pending':
          stats.pending++;
          stats.pendingAmount += commission.amount || 0;
          break;
        case 'Paid':
          stats.paid++;
          stats.paidAmount += commission.amount || 0;
          break;
        case 'Cancelled':
          stats.cancelled++;
          stats.cancelledAmount += commission.amount || 0;
          break;
        default:
          break;
      }
    });
    
    return stats;
  } catch (error) {
    console.error('Error fetching commission stats:', error);
    throw new Error('Erreur lors de la récupération des statistiques');
  }
};

/**
 * Mark commission as paid
 * @param {string} commissionId - Commission ID
 * @param {Object} paymentDetails - Payment details (method, reference, etc.)
 * @returns {Promise<Object>} Updated commission
 */
export const markCommissionAsPaid = async (commissionId, paymentDetails = {}) => {
  try {
    const commissionRef = doc(firestore, 'commissions', commissionId);
    
    const updateData = {
      status: 'Paid',
      paidAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
      ...paymentDetails
    };
    
    await updateDoc(commissionRef, updateData);
    
    return await getCommissionById(commissionId);
  } catch (error) {
    console.error('Error marking commission as paid:', error);
    throw new Error('Erreur lors du marquage de la commission comme payée');
  }
};

export default {
  getAllCommissions,
  getCommissionById,
  getCommissionsByCompany,
  createCommission,
  updateCommission,
  deleteCommission,
  getCommissionStats,
  markCommissionAsPaid
};
